import type { ReactNode } from "react";

import type { EntityHistoryItem } from "@/api/admin/auditLog";
import { tryFormatIsoDate } from "@/utils/formatIsoToUTC";

export type FieldMapping = Readonly<Record<string, string>>;

export type FieldResolver = {
  getValue?: (snapshot: Record<string, unknown>) => unknown;
  formatValue?: (value: unknown) => string | null;
  renderValue?: (value: unknown) => ReactNode;
};

export type EntityHistoryChange = {
  field: string;
  fieldLabel: string;
  before: ReactNode;
  after: ReactNode;
};

export const resolveFieldValue = (
  snapshot: Record<string, unknown>,
  key: string,
  resolver?: FieldResolver
): unknown => {
  if (resolver?.getValue) {
    return resolver.getValue(snapshot);
  }

  return snapshot[key];
};

export const formatFieldValue = (
  value: unknown,
  resolver?: FieldResolver
): string | null => {
  if (resolver?.formatValue) {
    return resolver.formatValue(value);
  }

  return serializeValue(value);
};

export const renderFieldValue = (
  value: unknown,
  resolver?: FieldResolver
): ReactNode => {
  const formatted = formatFieldValue(value, resolver);

  if (formatted === null) {
    return "—";
  }

  const rendered = resolver?.renderValue?.(value);

  if (rendered !== null && rendered !== undefined) {
    return rendered;
  }

  return formatted;
};

export const formatSnapshotFieldValue = (
  snapshot: Record<string, unknown> | undefined,
  key: string,
  resolver?: FieldResolver
): string | null => {
  if (!snapshot) {
    return null;
  }

  if (!resolver) {
    return serializeValue(snapshot[key]);
  }

  return formatFieldValue(resolveFieldValue(snapshot, key, resolver), resolver);
};

export const getChanges = (
  log: EntityHistoryItem,
  mapKeyToName?: FieldMapping,
  fieldResolvers?: Record<string, FieldResolver>
): EntityHistoryChange[] => {
  const before = log.before ?? {};
  const after = log.after ?? {};

  const fields =
    mapKeyToName && Object.keys(mapKeyToName).length > 0
      ? Object.keys(mapKeyToName)
      : Array.from(new Set([...Object.keys(before), ...Object.keys(after)]));

  return fields
    .map(key => {
      const resolver = fieldResolvers?.[key];
      const beforeValue = resolveFieldValue(before, key, resolver);
      const afterValue = resolveFieldValue(after, key, resolver);

      return {
        field: key,
        fieldLabel: mapKeyToName?.[key] ?? key,
        beforeFormatted: formatFieldValue(beforeValue, resolver),
        afterFormatted: formatFieldValue(afterValue, resolver),
        beforeValue,
        afterValue,
        resolver,
      };
    })
    .filter(change => change.beforeFormatted !== change.afterFormatted)
    .map(({ field, fieldLabel, beforeValue, afterValue, resolver }) => ({
      field,
      fieldLabel,
      before: renderFieldValue(beforeValue, resolver),
      after: renderFieldValue(afterValue, resolver),
    }));
};

const serializeValue = (value: unknown): string | null => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === "boolean" || typeof value === "number") {
    return String(value);
  }

  if (typeof value === "string") {
    return tryFormatIsoDate(value);
  }

  if (Array.isArray(value)) {
    if (
      value.every(item => typeof item === "string" || typeof item === "number")
    ) {
      return value.join(", ");
    }

    return value
      .map(item => serializeValue(item))
      .filter(Boolean)
      .join("\n");
  }

  if (typeof value === "object") {
    return Object.entries(value)
      .map(([key, val]) => `${key}: ${serializeValue(val) ?? "—"}`)
      .join(", ");
  }

  return String(value);
};
